import { Box, Button } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ArrowForwardIos } from "@mui/icons-material";
import { useStyles } from "./style";
import { NewLearningRequestStep } from "utils/types";
import { SURVEY_QUESTION_TYPE, SurveyType } from "utils/graphql/Global";
import { useTranslation } from "react-i18next";
import { Loader } from "components/loader/loader";
import { SURVEY_DATA, SUBMIT_NLR_DETAILS } from "utils/queries";
import * as SurveyTypes from "utils/graphql/SurveyData";
import { useQuery, useMutation } from "@apollo/client/react";
import { SurveyContainer } from "../../../question-types/survey-container/survey-container";
import { normalizeQuestionText } from "utils/helpers";
import portalSettingsManager from "utils/portalSettingsManager";
import { mapToResponse, validateAllQuestions, validateDateRange } from "../../../shared/validation";
import { useAppDispatch } from "store";
import { logger } from "utils/logger";
import {
  focusFirstError,
  NEW_LEARNING_ACTION_TYPE,
  NEW_LEARNING_ALERT_TYPE,
  showAction,
} from "../../../shared/newLearningHelper";

interface CommissioningDetailsProps {
  onNext: (currentStepParam: NewLearningRequestStep) => void;
  onSubmitSuccess?: () => void;
  isMobile?: boolean;
  userContext: any;
  setDialogTitle?: (title: string) => void;
}

export const CommissioningDetails: FC<CommissioningDetailsProps> = ({
  onNext,
  onSubmitSuccess,
  isMobile = false,
  userContext,
  setDialogTitle,
}) => {
  const classes = useStyles({ isMobile });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dateQuestionIdsRef = useRef<{ startId?: string; endId?: string }>({});
  const [questions, setQuestions] = useState<SurveyTypes.SurveyData_survey_questions[]>([]);
  const [survey, setSurvey] = useState<SurveyTypes.SurveyData_survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, any>>({});

  useEffect(() => {
    setDialogTitle?.(t("commissioning-title"));
  }, [setDialogTitle, t]);

  const handleAnswerChange = useCallback(
    (questionId: string, value: any) => {
      const { startId, endId } = dateQuestionIdsRef.current;
      const isDateQuestion = startId && endId && (questionId === startId || questionId === endId);

      setAnswers((prev) => {
        const updated = { ...prev, [questionId]: value };

        if (isDateQuestion) {
          const dateRangeError = validateDateRange(
            updated[startId]?.value?.trim(),
            updated[endId]?.value?.trim(),
            t("end_date_alert", { defaultValue: "End date cannot be before start date." }),
          );
          setValidationErrors((prev) => ({
            ...prev,
            [endId]: dateRangeError,
            ...(questionId !== endId && { [questionId]: {} }),
          }));
        } else {
          setValidationErrors((prevErrors) => ({ ...prevErrors, [questionId]: {} }));
        }

        return updated;
      });
    },
    [t],
  );

  const { loading, data } = useQuery<SurveyTypes.SurveyData>(SURVEY_DATA, {
    variables: {
      surveyType: SurveyType.NLR,
    },
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  const [submitNlrDetails] = useMutation(SUBMIT_NLR_DETAILS);

  const handleSubmit = useCallback(async () => {
    const learnerAnswers = mapToResponse(answers);

    const submitNlrSurveyInput = {
      surveyId: survey?.surveyId,
      learnerAnswers,
    };

    showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
      show: true,
      message: t("saving_commissioning_details"),
    });
    try {
      await submitNlrDetails({
        variables: {
          input: submitNlrSurveyInput,
        },
      });
      logger.info("Commissioning details submitted successfully");
      onSubmitSuccess?.();


      onNext(NewLearningRequestStep.RequestSubmitted);
    } catch {
      logger.error("Failed to submit commissioning details", {
        surveyId: survey?.surveyId,
      });
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.ALERT_SHOW, {
        type: NEW_LEARNING_ALERT_TYPE.ERROR,
        title: t("commissioning-title"),
        message: t("commissioning_details_save_errorve_error"),
      });
    } finally {
      showAction(dispatch, NEW_LEARNING_ACTION_TYPE.LOADER_SHOW_AND_HIDE, {
        show: false,
      });
    }
  }, [answers, survey?.surveyId, submitNlrDetails, onNext, onSubmitSuccess, dispatch, t]);

  const handleNext = useCallback(() => {
    if (!questions.length) return;

    const { isValid, errors } = validateAllQuestions(questions, answers);
    let allValid = isValid;
    const mergedErrors: Record<string, any> = { ...errors };

    const { startId, endId } = dateQuestionIdsRef.current;
    if (startId && endId) {
      const dateRangeError = validateDateRange(
        answers[startId]?.value?.trim(),
        answers[endId]?.value?.trim(),
        t("end_date_alert", { defaultValue: "End date cannot be before start date." }),
      );
      if (dateRangeError.showInvalidInputError) {
        mergedErrors[endId] = { ...(mergedErrors[endId] || {}), ...dateRangeError };
        allValid = false;
      }
    }

    if (!allValid) {
      setValidationErrors(mergedErrors);
      // This helps keyboard and screen reader users immediately know where the problem is
      focusFirstError(mergedErrors, questionRefs.current);
      return;
    }

    handleSubmit();
  }, [questions, answers, t, handleSubmit]);

  useEffect(() => {
    if (data) {
      setSurvey(data.survey);
      setQuestions(data.survey.questions);
      // Clear validation errors when survey loads
      setValidationErrors({});
      const dateFields = data.survey.questions.filter(
        (q) => q.questionType === SURVEY_QUESTION_TYPE.DateField,
      );
      dateQuestionIdsRef.current = {
        startId: dateFields.find((q) => { const t = normalizeQuestionText(q.htmlText); return t.includes("start") && t.includes("date"); })?.questionId,
        endId: dateFields.find((q) => { const t = normalizeQuestionText(q.htmlText); return t.includes("end") && t.includes("date"); })?.questionId,
      };
    }
  }, [data]);

  return (
    <>
      <Box className={classes.container}>
        <Loader isDisplay={loading}></Loader>
        <h2 className={classes.cardTitle}>{t("commissioning-title")}</h2>
        <Box className={classes.headerDescription}>{t("commissioning-description")}</Box>
        <Box className={classes.contentCard}>
          <Box className={classes.userInfoWrapper}>
            <Box className={classes.userInfoSection}>
              <p className={classes.userInfoTitle}>{t("your-information")}</p>
              <Box className={classes.userInfoGrid}>
                <Box>
                  <span className={classes.userInfoLabel}>{t("commissioning-form-name")}: </span>{" "}
                  {`${userContext.userContext.firstName} ${userContext.userContext.lastName}`}
                </Box>
                <Box>
                  <span className={classes.userInfoLabel}>{t("department")}: </span>{" "}
                  {userContext.userContext.departmentName}
                </Box>
                <Box>
                  <span className={classes.userInfoLabel}>{t("commissioning-form-email")}: </span>{" "}
                  {userContext.userContext.learnerEmail}
                </Box>
              </Box>
            </Box>
          </Box>

          {survey?.questions?.map((question, index) => {
            return (
              <div
                key={question.questionId}
                ref={(el) => {
                  questionRefs.current[question.questionId] = el;
                }}
              >
                <Box className={classes.questionItem}>
                  <SurveyContainer
                    question={question}
                    questionIndex={index}
                    answer={answers[question.questionId]}
                    onAnswerChange={handleAnswerChange}
                    validationErrors={validationErrors[question.questionId] || {}}
                    minCharacters={
                      portalSettingsManager.surveyCharacterMinMaxThreshold
                        ?.surveyLearnerCommentMinLength
                    }
                    maxCharacters={
                      portalSettingsManager.surveyCharacterMinMaxThreshold
                        ?.surveyLearnerCommentMaxLength
                    }
                  />
                </Box>
              </div>
            );
          })}

          <Box className={classes.buttonGroup}>
            <Button
              type="button"
              variant="contained"
              onClick={handleNext}
              endIcon={<ArrowForwardIos />}
              className={classes.primaryButton}
            >
              {t("submit-request")}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
};
